<template>
  <div class="category-panel">
    <div class="panel-header">
      <el-link type="primary" :underline="false" class="panel-title" @click="$emit('open-dictionary-info')">属性字典</el-link>
    </div>

    <div class="search-bar">
      <el-input
        v-model="filterName"
        placeholder="搜索分类"
        size="small"
        prefix-icon="el-icon-search"
        @input="handleFilter"
      ></el-input>
    </div>

    <el-tree
      v-if="categories.length"
      ref="categoryTree"
      :data="categories"
      node-key="id"
      :props="{ children: 'children', label: 'name' }"
      :highlight-current="true"
      :expand-on-click-node="false"
      default-expand-all
      class="category-tree"
      :empty-text="'暂无分类'"
      :filter-node-method="filterNode"
    >
      <span
        slot-scope="{ node, data }"
        class="tree-node"
        :class="{ 'is-disabled': !data.enableFlag }"
        @click="handleSelectNode(data)"
      >
        <span class="tree-node-label" :title="data.name">{{ data.name }}</span>
        <span class="tree-node-actions">
          <!-- 平台分类：新增子分类 -->
          <template v-if="data.source === 'PLATFORM'">
            <i v-if="!data.hasAttribute && $withPermission('attributeDict_category_add')" class="el-icon-circle-plus-outline node-icon" title="新增子分类" @click.stop="handleAddChild(data)"></i>
          </template>
          <!-- 租户分类：新增子分类、编辑、删除 -->
          <template v-else>
            <i v-if="!data.hasAttribute && $withPermission('attributeDict_category_add')" class="el-icon-circle-plus-outline node-icon" title="新增子分类" @click.stop="handleAddChild(data)"></i>
            <i v-if="$withPermission('attributeDict_category_update')" class="el-icon-edit-outline node-icon" title="编辑" @click.stop="handleEditTenant(data)"></i>
            <i v-if="$withPermission('attributeDict_category_del')" class="el-icon-delete node-icon node-icon-delete" title="删除" @click.stop="handleDeleteTenant(data)"></i>
          </template>
        </span>
      </span>
    </el-tree>

    <EditCategoryDialog ref="editCategoryDialog" @success="loadTree" />
  </div>
</template>

<script>
import {
  getAttributeCategoryTree,
  deleteAttributeCategory,
} from '@/api/attributeDict.js';
import EditCategoryDialog from './EditCategoryDialog.vue';

export default {
  name: 'CategoryPanel',
  components: { EditCategoryDialog },
  data () {
    return {
      filterName: '',
      categories: [],
      selectedId: null,
      selectedSource: null,
    };
  },
  methods: {
    handleFilter () {
      this.$refs.categoryTree && this.$refs.categoryTree.filter(this.filterName);
    },
    filterNode (value, data) {
      if (!value) return true;
      return data.name.includes(value);
    },
    async loadTree () {
      try {
        const res = await getAttributeCategoryTree({});
        this.categories = res?.body || [];
        if (!this.selectedId && this.categories.length) {
          this.$nextTick(() => this.handleSelectNode(this.categories[0]));
        }
      } catch (err) {
        console.error('[CategoryPanel] loadTree failed:', err);
      }
    },
    handleSelectNode (data) {
      this.selectedId = data.id;
      this.selectedSource = data.source;
      this.$nextTick(() => {
        this.$refs.categoryTree && this.$refs.categoryTree.setCurrentKey(data.id);
      });
      this.$emit('category-change', { ...data });
    },
    clearSelection () {
      this.selectedId = null;
      this.selectedSource = null;
      this.$nextTick(() => {
        this.$refs.categoryTree && this.$refs.categoryTree.setCurrentKey(null);
      });
    },
    // 新增一级租户分类（根节点）
    handleAddRoot () {
      this.$refs.editCategoryDialog.open({ title: '新增一级分类' });
    },
    // 在平台或租户节点下新增租户子分类
    handleAddChild (data) {
      const isPlatformParent = data.source === 'PLATFORM';
      this.$refs.editCategoryDialog.open({
        parentId: data.id,
        parentIsPlatformFlag: isPlatformParent,
        title: `新增「${data.name}」的子分类`,
      });
    },
    // 我的分类：编辑
    handleEditTenant (data) {
      this.$refs.editCategoryDialog.open({ id: data.id, name: data.name, description: data.description, parentId: data.parentId, parentIsPlatformFlag: data.parentIsPlatformFlag, title: '编辑分类' });
    },
    // 我的分类：删除
    handleDeleteTenant (data) {
      this.$confirm(`确定要删除分类「${data.name}」吗？`, '删除分类', {
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(() => {
        deleteAttributeCategory({ id: data.id }).then(res => {
          if (res.heads.code === 200) {
            this.$message.success('删除成功');
            this.loadTree();
            this.$emit('category-change', null);
          }
        });
      }).catch(() => {});
    },
  },
  created () {
    this.loadTree();
  },
};
</script>

<style lang="scss" scoped>
.category-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  border-right: 1px solid #DFE2E8;
  overflow-y: auto;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 12px 8px;
}

.panel-title {
  font-size: 14px;
  font-weight: 600;
}

.search-bar {
  padding: 0 12px 8px;
}

.category-tree {
  padding: 4px 8px;
  font-size: 14px;
  color: rgba(36, 40, 50, 0.80);
}

.tree-node {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0 4px;
  cursor: pointer;
}

.tree-node-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tree-node.is-disabled > .tree-node-label {
  color: #C0C4CC;
}

.tree-node-actions {
  display: flex;
  align-items: center;
  margin-left: 4px;
  flex-shrink: 0;
  gap: 2px;
}

.node-icon {
  font-size: 14px;
  color: #d3d3d3;
  cursor: pointer;
  padding: 0 2px;

  &:hover { color: #5195EB; }
  &.node-icon-delete:hover { color: #F56C6C; }
}

/deep/ .el-tree-node__content {
  height: 32px;
  line-height: 32px;
  border-radius: 4px;
}

/deep/ .el-tree-node.is-current > .el-tree-node__content {
  background: rgba(15, 137, 255, 0.08);
  border-radius: 4px;
  color: #5195EB;
}

/deep/ .el-tree-node.is-current > .el-tree-node__content .tree-node.is-disabled > .tree-node-label {
  color: #C0C4CC;
}

/deep/ .el-tree-node__expand-icon::before {
  content: "\e6e0";
}

/deep/ .el-tree-node.is-current > .el-tree-node__content > .el-tree-node__expand-icon {
  color: #5195EB;
}

/deep/ .el-tree-node__expand-icon.is-leaf {
  opacity: 0;
}
</style>
